namespace Solovis.Reports.Parameters {

  interface Investment {
    label: string;
    investmentId: number;
    resourceId: number;
  }

  export class InvestmentParameterControl extends Solovis.UI.BaseControl {

    public initialParameterValue;
    public currentInvestment;

    public selectedInvestmentLabel;
    public selectInvestmentLink;

    public _selectionDialog: Solovis.UI.Dialog;

    private _portalServices: Solovis.Portal.IPortalServices;

    static hasNewRender = true;
    private _selectRef;

    constructor(options) {
      super(options);

      this._portalServices = options.portalServices;

      this.initialParameterValue = (typeof options.initialValue != "undefined") ? options.initialValue : null;
      this.currentInvestment = this.initialParameterValue;
    }

    public static preload(newValue, callback, portalServices: Solovis.Portal.IPortalServices) {
      if (!portalServices) {
        portalServices = new Solovis.Portal.SitePortalServices();
      }

      if (newValue && (newValue.investmentId || newValue.resourceId || newValue.investmentCode)) {
        var loadOptions: any = { investment: {} };
        if (newValue.investmentId)
          loadOptions.investment.id = newValue.investmentId;
        if (newValue.resourceId)
          loadOptions.investment.resourceId = newValue.resourceId;
        if (newValue.investmentCode)
          loadOptions.investment.code = newValue.investmentCode;

        portalServices.reportDataLoader().loadDataSetAsync("/Solovis/Metadata/ParameterLoad",
          loadOptions,
          function (response, success) {
            if (success) {
              var dataSet = response.result.dataSet;
              var investmentData = dataSet.investment ? dataSet.investment : null;
              callback(investmentData, true);
            }
            else {
              callback(null, false);
            }
          });
      }
      else {
        callback(null, true);
      }
    }

    public static preloadMultiple(
      values: any[],
      callback: (items: any[], success: boolean) => void,
      portalServices: Solovis.Portal.IPortalServices) {

      EntityOrInvestmentParameterControl.preloadMultiple(values, callback, portalServices);
    }

    public static shouldPreload(value) {
      if (!value || (value.investmentId && value.resourceId && value.label)) {
        // Already loaded.
        return false;
      }

      return true;
    }

    public static serializeParameterValue(value): any {
      if (value) {
        if (value.investmentId) {
          return { investmentId: value.investmentId };
        }
        else if (value.resourceId) {
          return { resourceId: value.resourceId };
        }
        else if (value.investmentCode) {
          return { investmentCode: value.investmentCode };
        }
      }

      return null;
    }

    public static getLabel(value: Investment) {
      return value?.label 
    }

    public doRender() {
      if (this.options.renderMode === 'modern') {
        this.doRenderNew();
      } else {
        this.doRenderClassic();
      }
    }

    private async doRenderNew() {
      var self = this;
      const target = this.container[0];

      const { fetchInvestments } = await (window as any).importShim('@solovis/pa-utils');

      await Solovis.UX.Utils.placeComponent(
        "AsyncSelect",
        target,
        {
          cacheOptions: true,
          defaultValue: (self.options as any).initialValue || undefined,
          getOptionLabel: InvestmentParameterControl.getLabel,
          getOptionValue: (option) => {
            return option.investmentId;
          },
          loadOn: "input",
          loadOptions: (search: string) => new Promise(async (resolve, reject) => {
            try {
              const options = await fetchInvestments({ filterText: search, maxItems: 10 });
              if (options.hasMoreData) {
                options.data.push({
                  label: "More Results",
                  investmentId: -1,
                  resourceId: -1,
                  originalQuery: { filterText: search, excludeDemotedItems: true },
                })
              }
              resolve(options.data);
            } catch (e) {
              reject(e);
            }
          }),
          onChange: (selectedItem) => {
            //if nothing selected don't notify
            if (!selectedItem) {
              return;
            }
            if (selectedItem.label === "More Results") {
              fetchInvestments({ filterText: selectedItem.originalQuery.filterText, excludeDemotedItems: false, includeAll: true }).then(entities => {
                var data = entities.data;

                Solovis.UI.Dialogs.StringFilterListDialog.showDialog({
                  title: "Select Investment",
                  selectCallback: function (selectedItem) {
                    self._selectRef.setValue(selectedItem);
                  },
                  stringFilterListControlOptions: {
                    listItems: data,
                    itemDivGenerator: function (itemHolder, filterOptions) {
                      var itemDiv = $("<div>").text(itemHolder.item.label);
                      itemDiv.prepend(Solovis.UI.Icons.createInvestmentIcon({ "padding-right": "10px" }));
                      return itemDiv;
                    }
                  }
                });
              });
              setTimeout(() => { //wait then clear the more results
                self._selectRef.clearValue();
              }, (0));
              return;
            }
            self.currentInvestment = selectedItem;
            self.notifyParameterValueChanged();
          },
          ref: (ref) => {
            self._selectRef = ref;
          }
        }
      );
    }

    public doRenderClassic() {
      var self = this;

      this.selectedInvestmentLabel = $("<a>").appendTo(this.container);
      Solovis.UI.PopupLinkPanel.onElementClick(this.selectedInvestmentLabel, function (e) {
        if (self.currentInvestment && self.currentInvestment.investmentId) {
          // NOTE: Do not use autoClickSingleItem here. It's not a good experience if someone is trying
          //   to click the edit link and ends up jumping to another page instead.
          Solovis.UI.PopupLinkPanel.showLinks({
            offset: { left: e.pageX, top: e.pageY },
            links: self._portalServices.reportLinker().getInvestmentLinks(self.currentInvestment.investmentId)
          });
        }
      });
      this.selectInvestmentLink = $("<a>").html("<span class='fa fa-pencil'></span>").appendTo(this.container);

      this.selectInvestmentLink.click(function () {
        self._toggleSelectorDialog(function (investment) {
          self._setCurrentInvestmentData(investment);
          self.notifyParameterValueChanged();
        });
      });

      // Set initial parameter value text.
      if (this.currentInvestment) {
        this._setCurrentInvestmentData(this.currentInvestment);
      }

      // Setup drop handler for Solovis reference.
      Solovis.Site.DragDropManager.setupSolovisRefDrop(this.container, function (solovisRef) {
        if (!solovisRef.type || solovisRef.type == "investment" || solovisRef.type == "entityOrInvestment") {
          self.setParameterValue(solovisRef.value);
        }
      });
    }

    public _setCurrentInvestmentData(investmentData) {
      this.currentInvestment = investmentData;
      if (this.currentInvestment) {
        this.selectedInvestmentLabel.text(InvestmentParameterControl.getLabel(this.currentInvestment) + " ");
      }
      else {
        this.selectedInvestmentLabel.text("");
      }
    }

    public setParameterValue(newValue, more?) {
      Solovis.Reports.Parameters.ParameterControlHelper.handleSetParameterValue(
        this, InvestmentParameterControl, this._setCurrentInvestmentData, newValue, more);
    }

    public getParameterValue() {
      if (this.state < Solovis.UI.BaseControl.ControlState.afterInit) {
        return this.initialParameterValue ? this.initialParameterValue : null;
      }
      else if (this.currentInvestment) {
        return Solovis.deepCopy(this.currentInvestment);
      }
      else {
        return null;
      }
    }

    public notifyParameterValueChanged() {
      $(this).trigger("parameterValueChanged");
    }


    public _toggleSelectorDialog(selectionCallback) {
      var self = this;

      if (this._selectionDialog) {
        // We already have the selection dialog open, so close it.
        this._selectionDialog.close();
        this._selectionDialog = null;
        return;
      }

      var investmentSelectorControlHostDiv = $("<div>");
      var investmentSelectorControlHost = new Solovis.UI.ControlHost({ container: investmentSelectorControlHostDiv });

      var investmentSelectorControl = new Solovis.InvDataSelectors.InvestmentSelectorControl({
        controlHost: investmentSelectorControlHost,
        portalServices: this._portalServices,
        initialValue: this.currentInvestment,
        selectionCallback: function (investment) {
          selectionCallback(investment);
          self._selectionDialog.close();
        },
        cancelCallback: function () {
          self._selectionDialog.close();
        }
      });
      investmentSelectorControlHost.setCurrentControl(investmentSelectorControl);

      // Determine dialog position.        
      var linkOffset = $(this.selectedInvestmentLabel).offset();
      var linkHeight = $(this.selectInvestmentLink).outerHeight();
      var dialogPosition = {
        left: linkOffset.left,
        top: linkOffset.top + linkHeight + 5
      };

      var adjustedPosition = Solovis.UI.Dialog.autoAdjustSizeAndPosToViewport(dialogPosition, { width: 350, height: 150 });

      var minWidth = 150;
      var minHeight = 135;

      var getDesiredWidth = function () {
        var desiredWidth = Math.min($(window).width(), 450);
        return desiredWidth;
      };

      var getDesiredHeight = function () {
        var currentViewInfo = investmentSelectorControl.getCurrentViewInfo();
        switch (currentViewInfo.activeTabCode) {
          case "byName": return 135;

          case "byOwner":
          case "byHolding": {
            return Math.min($(window).height(), 320);
          }
        }

        return minHeight;
      };

      $(investmentSelectorControl).on("currentViewInfoChanged", function () {
        if (self._selectionDialog) {
          var desiredWidth = getDesiredWidth();
          var desiredHeight = getDesiredHeight();

          self._selectionDialog.width(desiredWidth);
          self._selectionDialog.height(desiredHeight);
          self._selectionDialog.autoAdjustToViewport();

          var dialogContentHeight = self._selectionDialog.getAvailableContentHeight();

          investmentSelectorControl.setAvailableHeight(dialogContentHeight);
        }
      });


      var desiredWidth = getDesiredWidth();

      this._selectionDialog = new Solovis.UI.Dialog({
        container: investmentSelectorControlHostDiv,
        ownerDialog: Solovis.UI.Dialog.findContainingDialog(this.container),

        title: false,
        autoOpen: true,
        destroyOnClose: true,
        actions: ["Close"],
        noAnimation: true,
        position: adjustedPosition,
        width: desiredWidth,
        minWidth: minWidth,
        minHeight: minHeight,

        buttons: [
          {
            label: "Clear",
            click: function () {
              selectionCallback(null);
              self._selectionDialog.close();
            }
          },
          {
            label: "Cancel",
            click: function () {
              self._selectionDialog.close();
            }
          }
        ],

        activate: function () {
          investmentSelectorControl.focus();

          var overallHeight = this.getAvailableContentHeight();
          investmentSelectorControl.setAvailableHeight(overallHeight);
        },

        resize: function () {
          var overallHeight = this.getAvailableContentHeight();
          investmentSelectorControl.setAvailableHeight(overallHeight);
        },

        deactivate: function () {
          self._selectionDialog = null;
        }
      });
    }
  }

}